import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import type { Gender } from '@yadviga/shared'
import { api, type PlaceSuggestion } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../ui/Button'
import { DateField, TimeField } from '../ui/DatePicker'
import { Modal } from '../ui/Modal'
import { PlaceAutocomplete } from '../ui/PlaceAutocomplete'

interface Props {
  open: boolean
  onClose: () => void
}

function placeFromUser(u: ReturnType<typeof useSession.getState>['user']): PlaceSuggestion | null {
  if (!u || u.birthPlaceName == null || u.birthPlaceLat == null || u.birthPlaceLon == null) {
    return null
  }
  return {
    id: 'saved',
    name: u.birthPlaceName,
    displayName: u.birthPlaceName,
    country: null,
    lat: u.birthPlaceLat,
    lon: u.birthPlaceLon,
    timezone: u.timezone,
  }
}

/**
 * Edit modal for the "Свои данные" block on /profile. Combines identity
 * (name, gender, language) and birth data into a single form so the user
 * edits them in one shot. BirthModal stays separate because it's used as a
 * "fill missing data" prompt in horoscope/natal/weekly flows.
 */
export function SelfDataModal({ open, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const user = useSession((s) => s.user)
  const jwt = useSession((s) => s.jwt)
  const setSession = useSession((s) => s.setSession)

  const [name, setName] = useState(user?.name ?? '')
  const [gender, setGender] = useState<Gender | null>(user?.gender ?? null)
  const [language, setLanguage] = useState<'ru' | 'en'>((user?.language ?? 'ru') as 'ru' | 'en')
  const [birthDate, setBirthDate] = useState(user?.birthDate ?? '')
  const [birthTime, setBirthTime] = useState(user?.birthTime ?? '')
  const [noTime, setNoTime] = useState(user?.birthDate != null && !user.birthTime)
  const [place, setPlace] = useState<PlaceSuggestion | null>(() => placeFromUser(user))

  useEffect(() => {
    if (!open || !user) return
    setName(user.name ?? '')
    setGender(user.gender ?? null)
    setLanguage((user.language ?? 'ru') as 'ru' | 'en')
    setBirthDate(user.birthDate ?? '')
    setBirthTime(user.birthTime ?? '')
    setNoTime(user.birthDate != null && !user.birthTime)
    setPlace(placeFromUser(user))
  }, [open, user])

  const save = useMutation({
    mutationFn: () =>
      api.updateProfile({
        name: name.trim(),
        gender: gender ?? undefined,
        language,
        ...(birthDate ? { birthDate } : {}),
        ...(birthDate ? { birthTime: noTime ? null : (birthTime || null) } : {}),
        ...(place
          ? {
              birthPlaceLat: place.lat,
              birthPlaceLon: place.lon,
              birthPlaceName: place.name,
              timezone: place.timezone,
            }
          : {}),
      }),
    onSuccess: async (updated) => {
      if (jwt) setSession({ user: updated, jwt })
      if (updated.language !== i18n.language) {
        await i18n.changeLanguage(updated.language)
      }
      toast.success(t('profile.saved'))
      void qc.invalidateQueries()
      onClose()
    },
  })

  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const dateValid = birthDate === '' || /^\d{4}-\d{2}-\d{2}$/.test(birthDate)

  return (
    <Modal open={open} onClose={onClose} title={t('profile.identity_title')}>
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('profile.name')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-2xl border border-sage-200 bg-white px-3 py-2 text-sage-800 outline-none focus:border-sage-500"
          />
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('profile.gender')}</span>
          <div className="grid grid-cols-3 gap-2">
            {(['female', 'male', 'other'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGender(g)}
                className={`rounded-2xl border px-2 py-2 text-sm transition ${
                  gender === g
                    ? 'border-sage-500 bg-sage-100 text-sage-900'
                    : 'border-sage-200 bg-white text-sage-600 hover:bg-sage-50'
                }`}
              >
                {t(`profile.gender_${g}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('profile.language')}</span>
          <div className="grid grid-cols-2 gap-2">
            {(['ru', 'en'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLanguage(l)}
                className={`rounded-2xl border px-2 py-2 text-sm transition ${
                  language === l
                    ? 'border-sage-500 bg-sage-100 text-sage-900'
                    : 'border-sage-200 bg-white text-sage-600 hover:bg-sage-50'
                }`}
              >
                {t(`profile.language_${l}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-sage-200/60 pt-3" />

        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('birth_modal.date')}</span>
          <DateField value={birthDate} onChange={setBirthDate} minAge={0} maxAge={100} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('birth_modal.time')}</span>
          <TimeField
            value={birthTime}
            onChange={(v) => { setBirthTime(v); setNoTime(false) }}
            disabled={noTime}
            minuteStep={5}
          />
          <label className="flex items-center gap-2 text-sm text-sage-600">
            <input
              type="checkbox"
              checked={noTime}
              onChange={(e) => setNoTime(e.target.checked)}
            />
            {t('birth_modal.no_time')}
          </label>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-sage-700">{t('birth_modal.place')}</span>
          <PlaceAutocomplete value={place} onChange={setPlace} language={lang} />
          <span className="text-xs text-sage-500">{t('birth_modal.place_hint')}</span>
        </label>

        {save.error && (
          <p className="text-sm text-copper">{(save.error as Error).message}</p>
        )}

        <Button
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!dateValid}
        >
          {t('actions.save')}
        </Button>
      </div>
    </Modal>
  )
}
